import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SearchSection } from '../SearchSection/SearchSection';
import { DocumentationFilters } from './DocumentationFilters/DocumentationFilters';
import { DocumentationTable } from './DocumentationTable';
import { SidePanel } from '../../ui/SidePanel/SidePanel';
import { useAppSelector } from '../../../hooks/redux';
import { useDispatch } from 'react-redux';
import {
  setDocumentationReqBody,
  setDocuments,
  setDocumentsLoading,
} from '../../../store/slices/documentationSlice';
import { getDocumentation } from '../../../services/DocumentationService';
import { setFromRequirement } from '../../../store/slices/requirementStepsSlice';
import { useDocumentSigningPolling } from '../../../hooks/useDocumentSigningPolling';

export function Documentation() {
  const dispatch = useDispatch();
  const { id: aiSystemId } = useParams<{ id: string }>();
  const {
    documentationReqBody,
    createAnnexIVDraftResponse,
    createEUDeclarationResponse,
    createFRIAResponse,
    documents,
    createDPIAResponse,
  } = useAppSelector((state) => state.documentation);

  const { sidePanelTitle, sidePanelContent } = useAppSelector(
    (state) => state.sidePanel,
  );

  const [searchValue, setSearchValue] = useState('');

  useDocumentSigningPolling({
    pollingInterval: 3000
  });

  const fetchDocumentation = async () => {
    try {
      dispatch(setDocumentsLoading(true));
      const response = await getDocumentation(documentationReqBody);

      if (documentationReqBody.offset === 0) {
        dispatch(setDocuments(response));
      } else {
        const existingIds = new Set(documents.map((doc) => doc.id));
        const mergedItems = [
          ...documents,
          ...response.items.filter((doc) => !existingIds.has(doc.id)),
        ];
        dispatch(
          setDocuments({
            items: mergedItems,
            total: response.total,
          }),
        );
      }
    } catch (error) {
      console.error('Error fetching documentation:', error);
    } finally {
      dispatch(setDocumentsLoading(false));
    }
  };

  // Set AI System ID from URL params on mount
  useEffect(() => {
    if (!aiSystemId) {
      return;
    }

    if (documentationReqBody.aiSystemId === aiSystemId) {
      return;
    }

    dispatch(
      setDocumentationReqBody({
        ...documentationReqBody,
        aiSystemId,
        offset: 0,
      }),
    );
  }, [aiSystemId, documentationReqBody, dispatch]);

  // useEffect(() => {
  //   const aiSysId = aiSystemId || documentationReqBody.aiSystemId;
  //   if (!aiSysId || createEUDeclarationResponse) {
  //     return;
  //   }

  //   let isCancelled = false;

  //   const loadExistingEUDeclaration = async () => {
  //     try {
  //       console.log('Loading existing EU Declaration draft:', aiSysId);
  //       const response = await getEUDeclarationByAiSystem(aiSysId);
  //       if (isCancelled) return;
  //       dispatch(setCreateEUDeclarationResponse(response));
  //       dispatch(
  //         setEUDeclarationFieldsState(mapEUDeclarationContentToFields(response))
  //       );
  //     } catch (error: any) {
  //       if (error?.response?.status !== 404) {
  //         console.error('Error loading existing EU Declaration draft', error);
  //       }
  //     }
  //   };

  //   loadExistingEUDeclaration();

  //   return () => {
  //     isCancelled = true;
  //   };
  // }, [
  //   aiSystemId,
  //   documentationReqBody.aiSystemId,
  //   createEUDeclarationResponse,
  // ]);

  useEffect(() => {
    // Only fetch if aiSystemId is set
    if (documentationReqBody.aiSystemId) {
      fetchDocumentation();
    }
  }, [documentationReqBody]);

  useEffect(() => {
    dispatch(
      setDocumentationReqBody({
        ...documentationReqBody,
        keyword: searchValue.length === 0 ? null : searchValue,
        offset: 0,
      }),
    );
  }, [searchValue]);

  useEffect(() => {
    dispatch(setFromRequirement(false));
  });

  return (
    <div className="w-full">
      <div className="flex flex-col gap-4">
        <div className="text-zinc-900 font-medium text-left">
          Compliance Documents
        </div>
        <SearchSection
          onSearchChange={setSearchValue}
          searchValue={searchValue}
        />
        <DocumentationFilters />
        <DocumentationTable />
        {sidePanelContent && (
          <SidePanel
            title={sidePanelTitle}
            content={sidePanelContent}
            annexId={
              sidePanelContent === 'ANNEX_IV'
                ? createAnnexIVDraftResponse?.id
                : undefined
            }
            declarationId={
              sidePanelContent === 'EU_DECLARATION'
                ? createEUDeclarationResponse?.id
                : undefined
            }
            friaId={
              sidePanelContent === 'FRIA' ? createFRIAResponse?.id : undefined
            }
            dpiaId={
              sidePanelContent === 'DPIA' ? createDPIAResponse?.id : undefined
            }
          />
        )}
      </div>
    </div>
  );
}
